import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Building2, MapPin, Phone, Save } from 'lucide-react';
import { apiClient } from '@/services/api/apiClient';

interface LocationData {
  rest_id: number;
  rest_nm: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  location_id: number;
  is_primary?: boolean;
  is_current?: boolean;
}

interface LocationEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  location: LocationData | null;
  onSuccess: () => void;
}

const LocationEditModal = ({ isOpen, onClose, location, onSuccess }: LocationEditModalProps) => {
  const [formData, setFormData] = useState({
    rest_nm: location?.rest_nm || '',
    address: location?.address || '',
    city: location?.city || '',
    state: location?.state || '',
    zip: location?.zip || '',
    phone: location?.phone || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [zipError, setZipError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  // Format phone number as (XXX) XXX-XXXX
  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format based on length
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else if (phoneNumber.length <= 10) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    } else {
      // Don't allow more than 10 digits
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  // Validate ZIP code (5 digits only)
  const validateZipCode = (value: string) => {
    const zipCode = value.replace(/\D/g, '');
    if (zipCode.length > 5) {
      setZipError('ZIP code must be 5 digits');
      return false;
    }
    if (zipCode.length > 0 && zipCode.length < 5) {
      setZipError('ZIP code must be 5 digits');
      return false;
    }
    setZipError('');
    return true;
  };

  // Update form data when location changes
  useEffect(() => {
    if (location) {
      setFormData({
        rest_nm: location.rest_nm || '',
        address: location.address || '',
        city: location.city || '',
        state: location.state || '',
        zip: location.zip || '',
        phone: formatPhoneNumber(location.phone || '')
      });
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!location) return;

    // Validate ZIP code
    if (!validateZipCode(formData.zip)) {
      return;
    }

    // Validate phone number (should have 10 digits)
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      setPhoneError('Phone number must be 10 digits');
      return;
    }

    setSaving(true);
    setError('');
    setPhoneError('');

    try {
      await apiClient.put(`/locations/${location.rest_id}/details`, formData);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to update location:', error);
      setError(error.response?.data?.detail || 'Failed to update location details');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // Format phone number as user types
      const formatted = formatPhoneNumber(value);
      setFormData({
        ...formData,
        phone: formatted
      });
      setPhoneError(''); // Clear error as user types
    } else if (name === 'zip') {
      // Only allow digits and max 5 characters
      const zipValue = value.replace(/\D/g, '').slice(0, 5);
      setFormData({
        ...formData,
        zip: zipValue
      });
      validateZipCode(zipValue);
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  if (!location) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 flex items-center justify-center p-4 z-50"
          >
            <div className="bg-white rounded-xl border border-gray-200 shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Building2 className="h-6 w-6 text-[#FDB913]" />
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">Edit Location Details</h2>
                      <p className="text-gray-500 text-sm">
                        Location #{location.location_id}
                        {location.is_primary && ': Primary'}
                        {location.is_current && (location.is_primary ? ' - Current' : ': Current')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5 text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6">
                <div className="space-y-4">
                  {/* Restaurant Name */}
                  <div>
                    <label htmlFor="rest_nm" className="block text-gray-700 text-sm mb-2">
                      Restaurant Name
                    </label>
                    <input
                      type="text"
                      id="rest_nm"
                      name="rest_nm"
                      value={formData.rest_nm}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913]"
                      placeholder="Enter restaurant name"
                    />
                  </div>

                  {/* Address */}
                  <div>
                    <label htmlFor="address" className="block text-gray-700 text-sm mb-2">
                      Street Address
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913]"
                      placeholder="123 Main St"
                    />
                  </div>

                  {/* City, State, Zip */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="city" className="block text-gray-700 text-sm mb-2">
                        City
                      </label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913]"
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <label htmlFor="state" className="block text-gray-700 text-sm mb-2">
                        State
                      </label>
                      <input
                        type="text"
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        required
                        maxLength={2}
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913]"
                        placeholder="ST"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="zip" className="block text-gray-700 text-sm mb-2">
                      ZIP Code
                    </label>
                    <input
                      type="text"
                      id="zip"
                      name="zip"
                      value={formData.zip}
                      onChange={handleInputChange}
                      required
                      maxLength={5}
                      className={`w-full px-4 py-2 bg-white border ${zipError ? 'border-red-400' : 'border-gray-300'} rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913]`}
                      placeholder="12345"
                    />
                    {zipError && (
                      <p className="text-red-600 text-xs mt-1">{zipError}</p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="block text-gray-700 text-sm mb-2">
                      Phone Number
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className={`w-full pl-10 pr-4 py-2 bg-white border ${phoneError ? 'border-red-400' : 'border-gray-300'} rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913]`}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    {phoneError && (
                      <p className="text-red-600 text-xs mt-1">{phoneError}</p>
                    )}
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-700 text-sm">{error}</p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-gray-700 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 px-4 py-2 bg-[#FDB913] hover:bg-[#FDB913]/90 rounded-lg transition-colors text-black font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default LocationEditModal;